/**
 * EnrichAllLiveBanner
 *
 * Rich "live parade" view of a bulk enrich-all job. Shows:
 *   - Who's being enriched right now (avatar + name + brokerage + i / N)
 *   - Row of 7 platform icons that pop in with their brand colour the moment
 *     that platform's URL is discovered for the current realtor
 *   - Strip of the last few realtors that just finished, each showing their
 *     filled platforms as a tiny colour row
 *
 * The backend job doc carries the live state on `summary.current` +
 * `summary.recent` — this component just reads it. Polling is handled by
 * JobsContext (which speeds up to 1.5s when an enrich_all is active).
 */
import React, { useEffect, useRef, useState } from "react";
import {
  Facebook,
  Instagram,
  Linkedin,
  Twitter,
  Youtube,
  Loader2,
  Check,
  Sparkles,
  HelpCircle,
  PlayCircle,
  Pause,
  Square,
  Trash2,
  PauseCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";

// Custom inline SVG icons for platforms that lucide-react doesn't ship.
// Kept in the file so we don't need a new dependency for two icons.
const TikTokIcon = ({ className = "", ...rest }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true" {...rest}>
    <path d="M19.321 5.562a5.124 5.124 0 0 1-3.414-1.267 5.124 5.124 0 0 1-1.537-3.036V.75h-3.4v13.887a2.907 2.907 0 0 1-2.905 2.906 2.907 2.907 0 0 1-2.905-2.906 2.907 2.907 0 0 1 2.905-2.905c.301 0 .592.046.867.132v-3.5a6.395 6.395 0 0 0-.867-.059A6.406 6.406 0 0 0 1.66 14.637 6.406 6.406 0 0 0 8.065 21.04a6.406 6.406 0 0 0 6.405-6.403V8.108a8.482 8.482 0 0 0 4.951 1.588V6.297a5.098 5.098 0 0 1-.1-.735z" />
  </svg>
);

const ThreadsIcon = ({ className = "", ...rest }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true" {...rest}>
    <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.028-3.579.878-6.432 2.523-8.482C5.845 1.205 8.598.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.968-3.898-5.994-8.312-6.026-2.91.021-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.175 1.43 1.781 3.631 2.696 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.964-.065-1.19.408-2.285 1.33-3.082.88-.76 2.119-1.207 3.583-1.291a13.853 13.853 0 0 1 3.02.142c-.126-.742-.375-1.332-.75-1.757-.513-.586-1.308-.883-2.359-.89h-.029c-.844 0-1.992.232-2.721 1.32L7.735 8.7c.98-1.454 2.568-2.256 4.478-2.256h.044c3.194.02 5.097 1.975 5.287 5.388.108.046.216.094.32.145 1.49.7 2.58 1.761 3.154 3.07.797 1.82.87 4.79-1.548 7.158-1.85 1.81-4.094 2.628-7.277 2.65Zm1.003-11.69c-.242 0-.487.007-.739.021-1.836.103-2.98.946-2.916 2.143.067 1.256 1.452 1.839 2.784 1.767 1.224-.065 2.818-.543 3.086-3.71a10.5 10.5 0 0 0-2.215-.221z" />
  </svg>
);

// Order + brand colours matched to enrich_util.SOCIAL_FIELDS on the backend.

// Order + brand colours matched to enrich_util.SOCIAL_FIELDS on the backend.
// Every platform gets its OWN distinct icon so reviewers can tell at a glance
// which social was found — previously TikTok + Threads shared lucide's
// Instagram icon, which made the row look like "Instagram appears 3 times".
const PLATFORMS = [
  { field: "facebook_url",  label: "Facebook",  short: "FB", Icon: Facebook,     colour: "#1877F2" },
  { field: "instagram_url", label: "Instagram", short: "IG", Icon: Instagram,    colour: "#E1306C" },
  { field: "linkedin_url",  label: "LinkedIn",  short: "IN", Icon: Linkedin,     colour: "#0A66C2" },
  { field: "twitter_url",   label: "X",         short: "X",  Icon: Twitter,      colour: "#000000" },
  { field: "tiktok_url",    label: "TikTok",    short: "TT", Icon: TikTokIcon,   colour: "#111827" },
  { field: "youtube_url",   label: "YouTube",   short: "YT", Icon: Youtube,      colour: "#FF0000" },
  { field: "threads_url",   label: "Threads",   short: "TH", Icon: ThreadsIcon,  colour: "#0F172A" },
];

function initials(name = "") {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function Avatar({ src, name, size = 40 }) {
  const [errored, setErrored] = React.useState(false);
  const px = { width: size, height: size };
  if (src && !errored) {
    return (
      <img
        src={src}
        alt={name}
        style={px}
        onError={() => setErrored(true)}
        className="rounded-full object-cover shrink-0 ring-2 ring-white shadow"
      />
    );
  }
  return (
    <div
      style={px}
      className="rounded-full shrink-0 ring-2 ring-white shadow flex items-center justify-center bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-sm font-semibold"
    >
      {initials(name)}
    </div>
  );
}

/**
 * Single platform pill. Grey by default; when its field lands in
 * `filled` it pops in with the brand colour + a scale animation.
 * `pending` renders as an amber ring (candidate awaiting review).
 * `missed` renders as a subtle strike-through grey.
 */
function PlatformPill({ platform, state, size = "md" }) {
  const { Icon, colour, label } = platform;
  const dims = size === "sm"
    ? "h-5 w-5 text-[10px]"
    : "h-8 w-8";
  const iconSize = size === "sm" ? "h-2.5 w-2.5" : "h-4 w-4";

  if (state === "filled") {
    return (
      <div
        key="filled"
        className={`${dims} rounded-full flex items-center justify-center shrink-0 shadow-md ring-2 ring-white platform-pop`}
        style={{ backgroundColor: colour, color: "white" }}
        title={`${label} — found`}
        data-testid={`enrich-live-pill-${platform.field}-filled`}
      >
        <Icon className={iconSize} />
      </div>
    );
  }
  if (state === "pending") {
    return (
      <div
        key="pending"
        className={`${dims} rounded-full flex items-center justify-center shrink-0 bg-white ring-2 ring-amber-400 platform-pop`}
        style={{ color: colour }}
        title={`${label} — candidate to review`}
        data-testid={`enrich-live-pill-${platform.field}-pending`}
      >
        <Icon className={iconSize} />
      </div>
    );
  }
  if (state === "missed") {
    return (
      <div
        key="missed"
        className={`${dims} rounded-full flex items-center justify-center shrink-0 bg-slate-100 text-slate-400 ring-1 ring-slate-200 relative pill-flip`}
        title={`${label} — searched, nothing found`}
        data-testid={`enrich-live-pill-${platform.field}-missed`}
      >
        <Icon className={iconSize} />
        <span className="absolute inset-0 flex items-center justify-center pill-strike-in">
          <span className="h-[2px] w-full bg-rose-400 rotate-45 rounded" />
        </span>
      </div>
    );
  }
  if (state === "searching") {
    return (
      <div
        key="searching"
        className={`${dims} rounded-full flex items-center justify-center shrink-0 bg-white ring-2 ring-slate-200 text-slate-400 animate-pulse`}
        title={`${label} — searching…`}
        data-testid={`enrich-live-pill-${platform.field}-searching`}
      >
        <Icon className={iconSize} />
      </div>
    );
  }
  // "idle" — platform not being searched (already had a value)
  return (
    <div
      key="idle"
      className={`${dims} rounded-full flex items-center justify-center shrink-0 bg-slate-50 ring-1 ring-slate-200 text-slate-300`}
      title={`${label} — already on file`}
      data-testid={`enrich-live-pill-${platform.field}-idle`}
    >
      <Icon className={iconSize} />
    </div>
  );
}

function pillState(field, current) {
  if (!current) return "idle";
  const filled = current.filled || [];
  const alreadyFilled = current.already_filled || [];
  const pending = current.pending || [];
  const missed = current.missed || [];
  const searched = current.searched || [];
  // Platforms already-on-file from a prior scan render as "filled" so the
  // user sees the FULL result for this realtor (not a row of ghost-grey
  // idle pills for the platforms we skipped). Newly-found platforms take
  // precedence in case both arrays somehow contain the same field.
  if (filled.includes(field)) return "filled";
  if (alreadyFilled.includes(field)) return "filled";
  if (pending.includes(field)) return "pending";
  if (missed.includes(field)) return "missed";
  if (searched.includes(field)) return "searching";
  return "idle";
}

const REVEAL_MS_PER_PILL = 200;
const HOLD_AFTER_REVEAL_MS = 1000;  // full-second "look at this result!" pause before swapping to the next realtor
function useStaggeredReveal(realtorKey, hasResults, count) {
  const [revealed, setRevealed] = useState(0);
  // Reset the counter every time we move to a fresh realtor so the next
  // reveal starts from 0.
  useEffect(() => {
    setRevealed(0);
  }, [realtorKey]);
  // Only run the tick timer once the backend has produced *some* results.
  // This gates the entire stagger on the batch being complete.
  useEffect(() => {
    if (!hasResults) return undefined;
    let tick = 0;
    const id = setInterval(() => {
      tick += 1;
      setRevealed(tick);
      if (tick >= count) clearInterval(id);
    }, REVEAL_MS_PER_PILL);
    return () => clearInterval(id);
  }, [realtorKey, hasResults, count]);
  return revealed;
}

/**
 * useSequencedDisplay — decouples what we show from what the backend has
 * moved on to. The scraper races through realtors at ~0.4-2 s each, but
 * we want the user to actually SEE each result: 7 pills stagger-reveal
 * at 200 ms each (1.4 s), then hold for 1 s, then swap to the next
 * realtor. Meanwhile the backend keeps working in the background — the
 * next realtor may already be done by the time we swap to it.
 *
 * The "recent" strip below the current row still tracks the backend
 * exactly (it's the actually-completed feed), so nothing is hidden;
 * this just paces the CURRENT-row spotlight.
 *
 * Contract: pass the raw `current` from backend; it returns:
 *   • `displayed`: the realtor to render as CURRENT (may lag backend)
 *   • `hasResults`: whether the displayed realtor's results are ready
 *   • `revealed`:  how many pills have been revealed so far (0..N)
 */
function useSequencedDisplay(backendCurrent) {
  const [displayed, setDisplayed] = useState(null);
  // Latest queued next-realtor (overwritten if backend advances further
  // while we're still animating the current one).
  const pendingRef = useRef(null);
  const holdTimerRef = useRef(null);

  // Feed backend updates into either displayed (merge same-realtor) or
  // pending (new realtor arrived while we're mid-animation).
  useEffect(() => {
    if (!backendCurrent) return;
    if (!displayed) {
      setDisplayed(backendCurrent);
      return;
    }
    if (backendCurrent.realtor_id === displayed.realtor_id) {
      // Same realtor — merge in new fields (results just arrived).
      setDisplayed({ ...backendCurrent });
      return;
    }
    // Different realtor — stash and let the reveal/hold timer swap it in.
    pendingRef.current = backendCurrent;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only the displayed realtor's identity matters; its other fields are merged in, not watched
  }, [backendCurrent, displayed?.realtor_id]);

  // Compute reveal state for the DISPLAYED realtor (not backend's current).
  // hasResults becomes true when the backend either produces some
  // `filled` / `missed` / `pending` (batch complete) OR marks the
  // realtor's status as done/failed. `searched` doesn't count — it's
  // populated at t=0 to announce which platforms WILL be searched, well
  // before any results land. So while a realtor is being scraped, all
  // pills correctly stay pulsing; the cascade only fires once the
  // backend has produced its verdict.
  const filledCount = (displayed?.filled || []).length;
  const missedCount = (displayed?.missed || []).length;
  const pendingCurCount = (displayed?.pending || []).length;
  const backendDone =
    displayed?.status === "done" || displayed?.status === "failed";
  const hasResults =
    filledCount + missedCount + pendingCurCount > 0 || backendDone;
  const revealed = useStaggeredReveal(
    displayed?.realtor_id || null,
    hasResults,
    PLATFORMS.length,
  );
  const fullyRevealed = hasResults && revealed >= PLATFORMS.length;

  // Once fully revealed AND we have a next realtor queued, schedule the
  // HOLD_AFTER_REVEAL_MS "look at this result" pause then swap. This
  // handles two orderings equally well:
  //   • reveal finishes BEFORE backend advances → we wait right here
  //     until backend queues a next realtor; the deps change (via the
  //     second effect below) kicks off the timer at that moment.
  //   • reveal finishes AFTER backend advances → pendingRef is already
  //     populated, timer starts immediately.
  useEffect(() => {
    if (!fullyRevealed) return undefined;
    if (holdTimerRef.current) return undefined;
    if (!pendingRef.current) return undefined;
    holdTimerRef.current = setTimeout(() => {
      holdTimerRef.current = null;
      if (pendingRef.current) {
        setDisplayed(pendingRef.current);
        pendingRef.current = null;
      }
    }, HOLD_AFTER_REVEAL_MS);
    return undefined;
  }, [fullyRevealed, backendCurrent]);

  // Cleanup timer on unmount.
  useEffect(() => () => {
    if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
  }, []);

  return { displayed, revealed, hasResults };
}

/**
 * Recent-completions strip — small avatars showing which realtors were
 * enriched just before the current one, plus a compact row of platform
 * dots so you can see at a glance what got filled.
 *
 * Memo'd with a shallow-length + last-id compare so this doesn't re-render
 * on every SSE tick — only when the `recent` list actually changes. Without
 * this the banner was thrashing the DOM every 600 ms, contributing to the
 * "PC fan speeding up" report from users.
 */
const RecentStrip = React.memo(function RecentStrip({ recent }) {
  if (!recent || recent.length === 0) return null;
  return (
    <div className="flex items-center gap-3 pt-2 mt-2 border-t border-blue-100 overflow-x-auto">
      <span className="text-[10px] uppercase tracking-wider text-blue-500 font-semibold shrink-0">
        Just finished
      </span>
      {recent.map((r) => (
        <div
          key={r.realtor_id}
          className="flex items-center gap-1.5 shrink-0"
          data-testid={`enrich-live-recent-${r.realtor_id}`}
          title={r.realtor_name}
        >
          <Avatar src={r.photo_url} name={r.realtor_name} size={22} />
          <span className="text-xs text-blue-900 max-w-[100px] truncate font-medium">
            {r.realtor_name}
          </span>
          <div className="flex items-center gap-0.5">
            {PLATFORMS.map((p) => {
              const filled = (r.filled || []).includes(p.field);
              const already = (r.already_filled || []).includes(p.field);
              const pending = (r.pending || []).includes(p.field);
              if (!filled && !already && !pending) return null;
              return (
                <PlatformPill
                  key={p.field}
                  platform={p}
                  state={filled || already ? "filled" : "pending"}
                  size="sm"
                />
              );
            })}
            {(r.filled || []).length === 0
              && (r.already_filled || []).length === 0
              && (r.pending || []).length === 0 ? (
              <span className="text-[10px] text-slate-500 italic">no socials</span>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}, (prev, next) => {
  // Cheap equality: same length + same trailing realtor IDs + same
  // filled/already_filled counts means the strip's rendered content is
  // identical. Skips re-renders during within-realtor progress ticks
  // (which only touch `current`, not `recent`).
  const a = prev.recent || [];
  const b = next.recent || [];
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i]?.realtor_id !== b[i]?.realtor_id) return false;
    if ((a[i]?.filled || []).length !== (b[i]?.filled || []).length) return false;
    if ((a[i]?.already_filled || []).length !== (b[i]?.already_filled || []).length) return false;
  }
  return true;
});

export default function EnrichAllLiveBanner({ job, onPause, onStop, onResume, onDelete }) {
  const summary = job.summary || {};
  const backendCurrent = summary.current || null;
  const recent = summary.recent || [];
  // Sequenced display — see useSequencedDisplay docblock. The backend
  // may have already moved on to the next realtor by the time we finish
  // showing this one's results; the hook holds the display steady for
  // reveal (1.4s) + a 1s "look at this" pause so the user actually sees
  // the outcome for every realtor even during a fast bulk run.
  const { displayed: current, hasResults, revealed: revealedPillCount } =
    useSequencedDisplay(backendCurrent);
  // Hide anything in `recent` that we're STILL displaying as current so
  // we don't briefly render the same realtor twice (once in the current
  // row, once in the just-finished strip). As soon as we swap to the
  // next realtor, this realtor pops into the recent strip normally.
  const filteredRecent = current?.realtor_id
    ? recent.filter((r) => r.realtor_id !== current.realtor_id)
    : recent;
  const processed = summary.processed ?? ((summary.successful ?? 0) + (summary.failed ?? 0));
  const total = current?.total ?? summary.targets ?? 0;
  const succeeded = summary.successful ?? 0;
  const failedCount = summary.failed ?? 0;
  const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
  // Paused vs running vs pause-in-flight distinction. The banner now serves
  // as the SINGLE source of truth for enrich_all state — running shows
  // Pause + Stop; paused shows Resume + Trash (no more separate yellow
  // resume chip above).
  const isPaused = job.status === "paused";
  const [pausing, setPausing] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const pauseFlag = !!summary.pause_requested;
  const stopFlag = !!summary.stop_requested;
  const showPausing = !isPaused && (pausing || pauseFlag);
  const showStopping = !isPaused && (stopping || stopFlag);

  const handlePause = async () => {
    if (showPausing || showStopping) return;
    setPausing(true);
    try {
      await onPause?.(job.id);
    } finally {
      // Keep spinner up — the backend loop still needs to close out the
      // current realtor before the job actually transitions to paused.
      // Once JobsContext picks up the status change, this banner re-renders
      // into the "paused" variant.
      setTimeout(() => setPausing(false), 8000);
    }
  };
  const handleStop = async () => {
    if (showPausing || showStopping) return;
    setStopping(true);
    try {
      await onStop?.(job.id);
    } finally {
      setTimeout(() => setStopping(false), 8000);
    }
  };
  const handleResume = async () => {
    if (resuming || deleting) return;
    setResuming(true);
    try {
      await onResume?.(job.id);
    } finally {
      setResuming(false);
    }
  };
  const handleDelete = async () => {
    if (resuming || deleting) return;
    setDeleting(true);
    try {
      await onDelete?.(job.id);
    } finally {
      setDeleting(false);
    }
  };

  // "N platforms found" text — counts both freshly-scraped `filled` and
  // pre-existing `already_filled` so the user sees the realtor's TRUE
  // social footprint, not just what this run happened to newly grab.
  const filledCountForCurrent =
    (current?.filled || []).length + (current?.already_filled || []).length;
  const pendingCount = summary.pending_count ?? 0;
  const remaining = isPaused ? Math.max(0, total - processed) : 0;

  // Palette flips to amber for paused so the visual is unambiguous.
  const paletteWrap = isPaused
    ? "border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50"
    : "border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50";
  const paletteIcon = isPaused ? "text-amber-700" : "text-blue-600";
  const paletteTitle = isPaused ? "text-amber-900" : "text-blue-900";
  const paletteMuted = isPaused ? "text-amber-700" : "text-blue-700";
  const paletteBarBg = isPaused ? "bg-amber-100" : "bg-blue-100";
  const paletteBarFill = isPaused
    ? "bg-gradient-to-r from-amber-400 to-yellow-500"
    : "bg-gradient-to-r from-blue-500 to-indigo-500";

  return (
    <div
      className={`rounded-lg border ${paletteWrap} px-4 py-3.5 shadow-sm`}
      data-testid={`enrich-live-banner-${job.id}`}
      data-status={job.status}
      role="status"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 shrink-0">
          {isPaused ? (
            <PauseCircle className={`h-5 w-5 ${paletteIcon}`} />
          ) : (
            <Loader2 className={`h-5 w-5 animate-spin ${paletteIcon}`} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className={`text-sm font-semibold ${paletteTitle} flex items-center gap-2 min-w-0`}>
              <Sparkles className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">
                {isPaused
                  ? `Enrichment paused${total ? ` · ${remaining.toLocaleString()} of ${total.toLocaleString()} still queued` : ""}`
                  : `Enriching realtors${total ? ` · ${processed}/${total}` : ""}`}
              </span>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <div className={`text-xs ${paletteMuted} tabular-nums mr-1`}>
                <span className="text-emerald-700 font-medium">{succeeded} ok</span>
                {failedCount ? <span className="text-rose-600 ml-2">· {failedCount} failed</span> : null}
              </div>
              {isPaused ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-50 shrink-0"
                    onClick={handleResume}
                    disabled={resuming || deleting}
                    title="Resume enrichment where it was paused"
                    data-testid={`enrich-live-resume-${job.id}`}
                  >
                    {resuming ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                    ) : (
                      <PlayCircle className="h-3.5 w-3.5 mr-1" />
                    )}
                    Resume
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Delete paused enrichment"
                    title="Delete this paused enrichment — the remaining queue will be discarded (you can run a new pass later)"
                    className="h-7 w-7 text-rose-700 hover:bg-rose-100 shrink-0 disabled:opacity-50"
                    onClick={handleDelete}
                    disabled={resuming || deleting}
                    data-testid={`enrich-live-delete-${job.id}`}
                  >
                    {deleting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </>
              ) : (
                <>
                  {onPause ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={showPausing ? "Pausing enrichment" : "Pause enrichment"}
                      title={
                        showPausing
                          ? "Pausing after current realtor…"
                          : "Pause after the current realtor — you can resume later"
                      }
                      className="h-7 w-7 text-amber-700 hover:bg-amber-100 shrink-0 disabled:opacity-50"
                      onClick={handlePause}
                      disabled={showPausing || showStopping}
                      data-testid={`enrich-live-pause-${job.id}`}
                    >
                      {showPausing ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Pause className="h-4 w-4" />
                      )}
                    </Button>
                  ) : null}
                  {onStop ? (
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={showStopping ? "Stopping enrichment" : "Stop enrichment"}
                      title={
                        showStopping
                          ? "Stopping after current realtor…"
                          : "Stop enrichment entirely — the queue will be discarded"
                      }
                      className="h-7 w-7 text-rose-700 hover:bg-rose-100 shrink-0 disabled:opacity-50"
                      onClick={handleStop}
                      disabled={showPausing || showStopping}
                      data-testid={`enrich-live-stop-${job.id}`}
                    >
                      {showStopping ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Square className="h-4 w-4" strokeWidth={2.5} />
                      )}
                    </Button>
                  ) : null}
                </>
              )}
            </div>
          </div>

          {/* Pause/stop hint that shows once the flag was set. Reassures the
              user their action was received while the current realtor is
              still finishing up (which can take 5-15s). */}
          {(showPausing || showStopping) && !isPaused ? (
            <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1.5 inline-flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              {showStopping
                ? "Stopping after the current realtor finishes — the queue will be cleared."
                : "Pausing after the current realtor finishes — you'll be able to resume from where we left off."}
            </div>
          ) : null}

          {/* Progress bar */}
          <div className={`mt-2 h-1.5 w-full rounded-full ${paletteBarBg} overflow-hidden`}>
            <div
              className={`h-full ${paletteBarFill} transition-[width] duration-500`}
              style={{ width: `${pct}%` }}
              data-testid={`enrich-live-progress-${job.id}`}
            />
          </div>

          {/* Current realtor row — hidden when paused (there's no "current"
              once the loop has exited). Paused state shows a compact
              "picked up where we left off" hint instead. */}
          {isPaused ? (
            <div className="mt-3 text-xs text-amber-800 flex items-center gap-1.5">
              <PauseCircle className="h-3.5 w-3.5" />
              {processed > 0
                ? `Paused after processing ${processed.toLocaleString()} realtor${processed === 1 ? "" : "s"} · click Resume to pick up where we left off.`
                : "Paused before the first realtor completed. Click Resume to start over."}
              {pendingCount ? (
                <span className="ml-1 text-amber-700">· {pendingCount.toLocaleString()} queued</span>
              ) : null}
            </div>
          ) : current ? (
            <div
              key={current.realtor_id}
              className="mt-3 flex items-center gap-3 min-w-0 realtor-slide-in"
              data-testid={`enrich-live-current-${current.realtor_id}`}
            >
              <Avatar src={current.photo_url} name={current.realtor_name} size={42} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate flex items-center gap-1.5">
                  {current.realtor_name}
                  {current.status === "done" ? (
                    <Check className="h-3.5 w-3.5 text-emerald-600" />
                  ) : null}
                </div>
                <div className="text-[11px] text-slate-500 truncate">
                  {current.brokerage_name || "—"}
                  {" · "}
                  {current.status === "in_progress"
                    ? "searching socials…"
                    : filledCountForCurrent > 0
                      ? `${filledCountForCurrent} platform${filledCountForCurrent === 1 ? "" : "s"} found`
                      : "no new socials"}
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0" data-testid={`enrich-live-pills-${current.realtor_id}`}>
                {PLATFORMS.map((p, idx) => (
                  <PlatformPill
                    key={p.field}
                    platform={p}
                    // While the backend is still searching (no results
                    // yet) every pill pulses in "searching" state. Once
                    // results arrive the stagger reveals each pill's
                    // actual state (filled / missed / pending) one at a
                    // time. Pills not yet revealed keep pulsing so the
                    // row never has a "dead" grey gap.
                    state={
                      hasResults && idx < revealedPillCount
                        ? pillState(p.field, current)
                        : "searching"
                    }
                    size="md"
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="mt-3 text-xs text-blue-700 flex items-center gap-1.5">
              <HelpCircle className="h-3.5 w-3.5" />
              Warming up the scraper — you&apos;ll see each realtor pop in here as they&apos;re processed.
            </div>
          )}

          {/* Recent completions — always shown so paused users can review
              the tail of what got done. */}
          <RecentStrip recent={filteredRecent} />
        </div>
      </div>
      {/* Scoped keyframes — Tailwind-safe (no arbitrary values in class names).
          The pop uses transform+opacity so it composites cleanly. */}
      <style>{`
        @keyframes platformPop {
          0%   { transform: scale(0.3); opacity: 0; }
          60%  { transform: scale(1.15); opacity: 1; }
          100% { transform: scale(1);    opacity: 1; }
        }
        .platform-pop { animation: platformPop 320ms cubic-bezier(0.34,1.56,0.64,1) both; }
        @keyframes pillFlip {
          0%   { transform: scale(0.6) rotate(-8deg); opacity: 0; }
          60%  { transform: scale(1.08) rotate(2deg); opacity: 1; }
          100% { transform: scale(1)   rotate(0deg);  opacity: 1; }
        }
        .pill-flip { animation: pillFlip 340ms cubic-bezier(0.34,1.56,0.64,1) both; }
        @keyframes pillStrikeIn {
          0%   { transform: scaleX(0); opacity: 0; }
          100% { transform: scaleX(1); opacity: 1; }
        }
        .pill-strike-in > span { animation: pillStrikeIn 260ms ease-out 160ms both; transform-origin: left center; }
        @keyframes realtorSlideIn {
          from { transform: translateY(6px); opacity: 0; }
          to   { transform: translateY(0);   opacity: 1; }
        }
        .realtor-slide-in { animation: realtorSlideIn 300ms ease-out both; }
      `}</style>
    </div>
  );
}
